// Part 2 (spec §1 211, M1; contract §2): the per-session room feature flags.
//
// `game_session_features(p_session_id)` returns each Part 2 flag's EFFECTIVE
// value for one session: the reliability_flags row OR `sessions_v2.type='test'`.
// Production sessions therefore stay on the legacy client paths until a flag is
// flipped, while test sessions on the shared DB get the new ones.
//
// Anything that isn't a clean answer (the RPC missing because the migration
// hasn't been applied, a network error, an unknown shape) reads as "all off",
// which is the legacy behaviour. A later refresh can switch a flag on.
//
// Lane B owns hooks/useSessionFeatures.js (the sheet's copy, refreshed every 60
// seconds and after any P0501). This module is the shared fetch it can build on;
// GameFlowProvider uses it directly so the step-authority switch does not wait
// on another lane.

import { supabase } from '@tasted/utils/supabase';

export const ROOM_FEATURE_FLAGS = Object.freeze([
  'room_step_authority',
  'room_active_seats',
  'room_help_sheet',
  'room_votes',
  'room_readmit',
  'room_restart_question',
  'room_reset_session',
]);

export const ROOM_FEATURES_OFF = Object.freeze(
  Object.fromEntries(ROOM_FEATURE_FLAGS.map((flag) => [flag, false]))
);

// How often a mounted game re-reads its flags (spec §1 211: every 60 seconds).
export const ROOM_FEATURES_REFRESH_MS = 60_000;

/** Normalise an RPC payload into `{ flag: boolean }` for every known flag. */
export function normaliseRoomFeatures(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return { ...ROOM_FEATURES_OFF };
  const out = {};
  for (const flag of ROOM_FEATURE_FLAGS) out[flag] = data[flag] === true;
  return out;
}

/**
 * Read the effective flags for a session.
 * @param {string} sessionCode
 * @returns {Promise<{ features: object, ok: boolean }>} `ok` is false when the
 *   read failed and `features` is the all-off default.
 */
export async function fetchRoomFeatures(sessionCode) {
  if (!sessionCode) return { features: { ...ROOM_FEATURES_OFF }, ok: false };
  try {
    const res = await supabase.rpc('game_session_features', { p_session_id: sessionCode });
    if (res?.error || !res?.data || typeof res.data !== 'object') {
      return { features: { ...ROOM_FEATURES_OFF }, ok: false };
    }
    return { features: normaliseRoomFeatures(res.data), ok: true };
  } catch {
    return { features: { ...ROOM_FEATURES_OFF }, ok: false };
  }
}

/** True when two flag objects agree on every known flag. */
export function sameRoomFeatures(a, b) {
  return ROOM_FEATURE_FLAGS.every((flag) => Boolean(a?.[flag]) === Boolean(b?.[flag]));
}
